// Founder decision 2026-09-21 (trees-3): the trees list is ranked FIRST by the
// tree's photo-URL PATTERN, so the trees whose photos actually render lead the
// list and the known-dead ones sink to the end. Within each rank group the order
// is NEWEST PLANTED first (planting_date descending), then id descending. Earlier
// (trees-2) the primary key was photographed-field then planted-date, which put
// the newest MIZAN trees on page 1 - but those have dead URLs, so page 1 was all
// "Coming soon". The pattern is a URL shape, not a per-file check: it cannot
// catch a one-off 404 inside a live pattern, only the whole known-dead shapes.
//
// Pure so the list slice and the page count share one ordering (a single sorted
// array feeds both currentPaginationData and the Pagination count in Project).

export type TreeLike = {
  id?: string | number;
  properties?: {
    photo?: unknown;
    fid?: string | number | null;
    planting_date?: string | null;
  } | null;
};

// "With a photo" = the photo field is a non-empty string. NULL, undefined,
// '' and whitespace-only all count as "without" (upstream stores '' for a
// missing photo, and the pin query already drops those - so on today's list
// this group is empty; kept correct for when the list ever carries them).
export const treeHasPhoto = (photo: unknown): boolean =>
  typeof photo === 'string' && photo.trim() !== '';

// ---------------------------------------------------------------------------
// TEMPORARY photo-URL pattern ranking - REMOVE THIS BLOCK (and the first key in
// compareTreesForList) once Fairchain restores the missing files / fixes the
// buckets. It exists only to keep dead-photo trees off the first pages while the
// upstream data is broken.
//
// Liveness of each pattern is the live evidence gathered for item 1, recorded in
// PROGRESS.md Question 36 (the Fairchain email): Limu photos live under
// `fairchain-images/trees/<hash>`; Mizan `*_QFieldCamera*.jpg` under
// `.../trees/files/` render; Mizan `trees_*.JPG` return 404 NoSuchKey; the
// `bac_tree` bucket does not serve to a browser; an empty field has no image.
// One place, one list - edit here, nowhere else.
// ---------------------------------------------------------------------------
const PHOTO_RANK = { live: 0, unknown: 1, dead: 2, empty: 3 } as const;

// `cqlNotLike` (dead patterns only): the GeoServer CQL LIKE fragment that matches
// the same shape as `test`, so getTreesQuery can EXCLUDE these upstream from the
// SAME list (deadPhotoCqlExclusion). One source - edit a pattern here and both
// the client sort rank and the server-side exclusion move together.
type PhotoPattern = {
  name: string;
  rank: number;
  test: (photo: string) => boolean;
  cqlNotLike?: string;
};

const PHOTO_URL_PATTERNS: readonly PhotoPattern[] = [
  // likely live (Q36) - lead the list
  {
    name: 'limu-hash', // fairchain-images/trees/<hex>, NOT under /files/
    rank: PHOTO_RANK.live,
    test: (p) => /\/fairchain-images\/trees\/[0-9a-f]{16,}/i.test(p),
  },
  {
    name: 'mizan-qfieldcamera',
    rank: PHOTO_RANK.live,
    test: (p) => /QFieldCamera/i.test(p),
  },
  // known dead (Q36) - sink to the end AND excluded upstream (getTreesQuery)
  {
    name: 'mizan-trees_', // .../trees/files/trees_YYYYMMDD.JPG -> 404 NoSuchKey
    rank: PHOTO_RANK.dead,
    test: (p) => /\/files\/trees_/i.test(p),
    cqlNotLike: '%/files/trees_%',
  },
  {
    name: 'bac_tree-bucket', // storage.googleapis.com/bac_tree/... -> not served
    rank: PHOTO_RANK.dead,
    test: (p) => /\/bac_tree\//i.test(p),
    cqlNotLike: '%/bac_tree/%',
  },
];

// The CQL fragment that excludes every known-dead photo pattern, e.g.
// "photo NOT LIKE '%/files/trees_%' AND photo NOT LIKE '%/bac_tree/%'".
// Empty string if no dead pattern carries a cqlNotLike (then callers add nothing).
// TEMPORARY, same removal note as PHOTO_URL_PATTERNS: delete once Fairchain fixes
// the files and getTreesQuery no longer needs to skip them.
export const deadPhotoCqlExclusion = (): string =>
  PHOTO_URL_PATTERNS.filter((p) => p.cqlNotLike)
    .map((p) => `photo NOT LIKE '${p.cqlNotLike}'`)
    .join(' AND ');

// Rank a tree by its photo-URL pattern: live patterns 0, an unclassified photo 1
// (between live and dead - we cannot assert it is dead), known-dead patterns 2,
// and an empty/NULL field 3 (always last).
export const photoPatternRank = (photo: unknown): number => {
  if (!treeHasPhoto(photo)) return PHOTO_RANK.empty;
  const p = String(photo);
  for (const pat of PHOTO_URL_PATTERNS) {
    if (pat.test(p)) return pat.rank;
  }
  return PHOTO_RANK.unknown;
};

// The tree's own id: the fid (the "TREE NR" shown on the card) when present,
// else the WFS feature id.
const idOf = (t: TreeLike): string | number => t.properties?.fid ?? t.id ?? '';

// Planting time in ms for "newest first". A missing or unparseable date sorts
// as the OLDEST (-Infinity), so undated trees fall to the end of their group.
const plantedTime = (t: TreeLike): number => {
  const d = t.properties?.planting_date;
  const ms = typeof d === 'string' ? Date.parse(d) : NaN;
  return Number.isFinite(ms) ? ms : -Infinity;
};

export const compareTreesForList = (a: TreeLike, b: TreeLike): number => {
  // FIRST key (trees-3, temporary): photo-URL pattern - live patterns lead, the
  // empty field and the known-dead patterns sink to the end.
  const ar = photoPatternRank(a.properties?.photo);
  const br = photoPatternRank(b.properties?.photo);
  if (ar !== br) return ar - br;

  const at = plantedTime(a);
  const bt = plantedTime(b);
  if (at !== bt) return bt - at; // planting_date descending (newest first)

  const av = idOf(a);
  const bv = idOf(b);
  const an = Number(av);
  const bn = Number(bv);
  if (Number.isFinite(an) && Number.isFinite(bn)) return bn - an; // id descending
  return String(bv).localeCompare(String(av), undefined, { numeric: true });
};

export const orderTreesForList = <T extends TreeLike>(trees: T[]): T[] =>
  [...trees].sort(compareTreesForList);

// How many whole pages at the END of the list are entirely photo-less, given
// the ordering above (with-photo first). Zero while the list holds only
// photographed trees. Pure, so the report can state the number from real data.
export const photolessTailPages = (trees: TreeLike[], perPage: number): number => {
  if (perPage <= 0) return 0;
  const withPhoto = trees.reduce((n, t) => n + (treeHasPhoto(t.properties?.photo) ? 1 : 0), 0);
  const totalPages = Math.ceil(trees.length / perPage);
  const pagesWithPhoto = Math.ceil(withPhoto / perPage);
  return Math.max(0, totalPages - pagesWithPhoto);
};
